/**
 * PlatformDrLlmPolicyPage — DR-level default LLM selection policy (ADR-042).
 *
 * SystemAdmin-only. Path: /settings/dr-llm-policy (?dr_code=eu-w1)
 *
 * Configures the platform-wide default policy every tenant in the DR
 * inherits (strategy, provider preference, allowed providers, fallback
 * behaviour, data residency, health threshold).
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { Globe, Loader2, Save, ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  useDrLlmPolicy,
  useUpdateDrLlmPolicy,
} from '@/features/llm-config/hooks/useLlmPolicy';
import type {
  DrLlmPolicyUpdate,
  HealthThreshold,
  PolicyStrategy,
} from '@/features/llm-config/types/policy';
import { POLICY_STRATEGIES } from '@/features/llm-config/types/policy';
import { PipelinePolicySections } from '@/features/llm-config/components/PipelinePolicySections';
import { TokenSavingsPanel } from '@/features/llm-config/components/TokenSavingsPanel';
import { STRATEGY_LABELS } from '@/features/llm-config/lib/policyLabels';
import { PROVIDER_METADATA } from '@/features/llm-config/types';
import { getErrorMessage } from '@/lib/api-client';

const DEFAULT_DR_CODE = 'eu-w1';

const ALL_PROVIDER_TYPES = Object.keys(PROVIDER_METADATA);

/** Formatea el timestamp ISO del API con el locale activo. */
function formatUpdatedAt(value: string, locale: 'en' | 'es'): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale === 'es' ? 'es-ES' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function PlatformDrLlmPolicyPage() {
  const { t, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const drCode = searchParams.get('dr_code') ?? DEFAULT_DR_CODE;

  const { data: policy, isLoading, error } = useDrLlmPolicy(drCode);
  const update = useUpdateDrLlmPolicy();

  const [form, setForm] = useState<DrLlmPolicyUpdate>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (policy) {
      setForm({
        strategy: policy.strategy,
        provider_preference: [...policy.provider_preference],
        allowed_providers: [...policy.allowed_providers],
        provider_weights: { ...policy.provider_weights },
        default_data_residency: policy.default_data_residency
          ? [...policy.default_data_residency]
          : null,
        fallback_enabled: policy.fallback_enabled,
        fallback_max_attempts: policy.fallback_max_attempts,
        health_threshold: policy.health_threshold,
        max_agent_steps: policy.max_agent_steps ?? 50,
        // Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1)
        routing_enabled: policy.routing_enabled,
        routing_mode: policy.routing_mode,
        routing_on_failure: policy.routing_on_failure,
        routing_confidence_threshold: policy.routing_confidence_threshold,
        routing_timeout_ms: policy.routing_timeout_ms,
        planning_on_failure: policy.planning_on_failure,
        grounding_check: policy.grounding_check,
        routing_budget_tokens: policy.routing_budget_tokens,
        optimization_enabled: policy.optimization_enabled,
        max_loop_iterations: policy.max_loop_iterations,
        frontier_agents_enabled: policy.frontier_agents_enabled,
        frontier_max_duration_minutes: policy.frontier_max_duration_minutes,
        frontier_max_concurrent_runs: policy.frontier_max_concurrent_runs,
        frontier_on_unavailable: policy.frontier_on_unavailable,
        plan_review: policy.plan_review,
        retrieval_gating_enabled: policy.retrieval_gating_enabled,
        foreground_timeout_seconds: policy.foreground_timeout_seconds,
        delegation_sync_timeout_seconds:
          policy.delegation_sync_timeout_seconds,
        delegation_background_timeout_seconds:
          policy.delegation_background_timeout_seconds,
        fanout_aggregation_timeout_seconds:
          policy.fanout_aggregation_timeout_seconds,
      });
    }
  }, [policy]);

  const toggleAllowed = (providerType: string) => {
    const current = form.allowed_providers ?? [];
    const next = current.includes(providerType)
      ? current.filter((p) => p !== providerType)
      : [...current, providerType];
    setForm({ ...form, allowed_providers: next });
  };

  const movePref = (idx: number, dir: -1 | 1) => {
    const pref = [...(form.provider_preference ?? [])];
    const target = idx + dir;
    if (target < 0 || target >= pref.length) return;
    [pref[idx], pref[target]] = [pref[target], pref[idx]];
    setForm({ ...form, provider_preference: pref });
  };

  const addToPreference = (providerType: string) => {
    const pref = form.provider_preference ?? [];
    if (pref.includes(providerType)) return;
    setForm({ ...form, provider_preference: [...pref, providerType] });
  };

  const removeFromPreference = (providerType: string) => {
    const pref = form.provider_preference ?? [];
    setForm({
      ...form,
      provider_preference: pref.filter((p) => p !== providerType),
    });
  };

  const handleSave = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await update.mutateAsync({ drCode, update: form });
      setSuccessMsg(t('policy.drSaved'));
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !policy) {
    const isNotFound =
      error instanceof Error &&
      (error.message.includes('404') ||
        error.message.toLowerCase().includes('not found') ||
        error.message.toLowerCase().includes('no policy configured'));

    if (isNotFound) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center space-y-4 max-w-md">
            <Globe className="h-10 w-10 text-gray-300 mx-auto" />
            <h2 className="text-xl font-semibold">
              {t('admin.drPolicy.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('policy.drNotFoundBefore')}{' '}
              <span className="font-mono">{drCode}</span>
              {t('policy.drNotFoundAfter')}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
        {t('policy.drLoadError', {
          drCode,
          message: error instanceof Error ? error.message : t('policy.unknownError'),
        })}
      </div>
    );
  }

  const preference = form.provider_preference ?? [];
  const allowed = form.allowed_providers ?? [];
  const weights = form.provider_weights ?? {};

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Globe className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">
            {t('admin.drPolicy.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('policy.drSubtitleBefore')}{' '}
            <span className="font-mono">{drCode}</span>
          </p>
        </div>
        <div className="ml-auto">
          <Input
            value={drCode}
            onChange={(e) =>
              setSearchParams({ dr_code: e.target.value || DEFAULT_DR_CODE })
            }
            className="w-32 font-mono text-sm"
          />
        </div>
      </div>

      {errorMsg && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="rounded-md border border-green-300 bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300">
          {successMsg}
        </div>
      )}

      {/* Strategy */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('policy.strategyTitle')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {POLICY_STRATEGIES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setForm({ ...form, strategy: s as PolicyStrategy })}
              className={`rounded-md border px-3 py-2 text-sm ${
                form.strategy === s
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {t(STRATEGY_LABELS[s])}
            </button>
          ))}
        </div>
      </section>

      {/* Policy enabled toggle */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {t('admin.drPolicy.enablePolicy')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.drPolicy.enablePolicyHint')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={form.policy_enabled ?? true}
              onChange={(e) => setForm({ ...form, policy_enabled: e.target.checked })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
      </section>

      {/* Allowed providers */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('policy.allowedProviders')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.allowedProvidersHint')}
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_PROVIDER_TYPES.map((pt) => {
            const meta =
              PROVIDER_METADATA[pt as keyof typeof PROVIDER_METADATA];
            const isAllowed = allowed.includes(pt);
            return (
              <button
                key={pt}
                type="button"
                onClick={() => toggleAllowed(pt)}
                className={`rounded-md border px-3 py-2 text-sm ${
                  isAllowed
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-300 dark:border-gray-600 text-gray-500'
                }`}
              >
                {meta?.displayName ?? pt}
              </button>
            );
          })}
        </div>
      </section>

      {/* Preference order */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('policy.preferenceOrder')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.preferenceOrderHint', { strategy: 'priority' })}
        </p>
        <ol className="space-y-2">
          {preference.map((pt, idx) => {
            const meta =
              PROVIDER_METADATA[pt as keyof typeof PROVIDER_METADATA];
            return (
              <li
                key={pt}
                className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2"
              >
                <span className="font-mono text-xs text-gray-500">
                  {idx + 1}
                </span>
                <span className="font-medium flex-1">
                  {meta?.displayName ?? pt}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={idx === 0}
                  aria-label={t('policy.moveUp')}
                  onClick={() => movePref(idx, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={idx === preference.length - 1}
                  aria-label={t('policy.moveDown')}
                  onClick={() => movePref(idx, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('policy.remove')}
                  onClick={() => removeFromPreference(pt)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </li>
            );
          })}
        </ol>
        {/* Add providers missing from the preference */}
        <div className="flex flex-wrap gap-2 pt-2">
          {ALL_PROVIDER_TYPES.filter((pt) => !preference.includes(pt)).map(
            (pt) => {
              const meta =
                PROVIDER_METADATA[pt as keyof typeof PROVIDER_METADATA];
              return (
                <Button
                  key={pt}
                  variant="outline"
                  size="sm"
                  onClick={() => addToPreference(pt)}
                >
                  + {meta?.displayName ?? pt}
                </Button>
              );
            },
          )}
        </div>
      </section>

      {/* Weights (for weighted strategy) */}
      {form.strategy === 'weighted' && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
          <h2 className="text-lg font-semibold">{t('policy.weightsTitle')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('policy.weightsHint', { strategy: 'weighted' })}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ALL_PROVIDER_TYPES.map((pt) => {
              const meta =
                PROVIDER_METADATA[pt as keyof typeof PROVIDER_METADATA];
              return (
                <div key={pt} className="flex items-center gap-3">
                  <label className="flex-1 text-sm">
                    {meta?.displayName ?? pt}
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    className="w-24"
                    value={weights[pt] ?? 0}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        provider_weights: {
                          ...weights,
                          [pt]: Number(e.target.value),
                        },
                      })
                    }
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Execution limits (REQ-CHAT-019 v2) */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
        <h2 className="text-lg font-semibold">{t('policy.executionLimitsTitle')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('policy.executionLimitsHint')}
        </p>
        <div className="flex items-center gap-2 text-sm">
          <span className="flex-1">{t('policy.maxAgentSteps')}:</span>
          <Input
            type="number"
            min="1"
            className="w-32"
            value={form.max_agent_steps ?? 50}
            onChange={(e) =>
              setForm({
                ...form,
                max_agent_steps: Number(e.target.value),
              })
            }
          />
        </div>
      </section>

      {/* Pipeline policy — Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1) */}
      <PipelinePolicySections
        mode="dr"
        value={form}
        onChange={(patch) => setForm({ ...form, ...patch })}
      />

      {/* Panel global de ahorro — Sprint 280 (REQ-CHAT-039 §7) */}
      <TokenSavingsPanel drCode={drCode} />

      {/* Fallback + Health + Residency */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
        <h2 className="text-lg font-semibold">{t('policy.fallbackTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.fallback_enabled}
              onChange={(e) =>
                setForm({ ...form, fallback_enabled: e.target.checked })
              }
            />
            {t('policy.fallbackEnable')}
          </label>

          <div className="flex items-center gap-2 text-sm">
            <span className="flex-1">{t('policy.fallbackMaxAttempts')}:</span>
            <Input
              type="number"
              min="0"
              max="5"
              className="w-24"
              value={form.fallback_max_attempts ?? 0}
              onChange={(e) =>
                setForm({
                  ...form,
                  fallback_max_attempts: Number(e.target.value),
                })
              }
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="flex-1">{t('policy.healthThreshold')}:</span>
            <select
              value={form.health_threshold ?? 'skip_degraded'}
              onChange={(e) =>
                setForm({
                  ...form,
                  health_threshold: e.target.value as HealthThreshold,
                })
              }
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <option value="skip_degraded">
                {t('policy.healthSkipDegraded')}
              </option>
              <option value="allow_degraded">
                {t('policy.healthAllowDegraded')}
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="flex-1">{t('policy.dataResidency')}:</span>
            <Input
              className="w-40"
              placeholder={t('policy.dataResidencyPlaceholder')}
              value={form.default_data_residency?.join(',') ?? ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                setForm({
                  ...form,
                  default_data_residency: value
                    ? value.split(',').map((s) => s.trim()).filter(Boolean)
                    : null,
                });
              }}
            />
          </div>
        </div>
      </section>

      {/* Current state badges */}
      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
        <Badge variant="outline">
          {t('policy.updatedAt', {
            date: formatUpdatedAt(policy.updated_at, locale),
          })}
        </Badge>
        {policy.updated_by && (
          <Badge variant="outline">
            {t('policy.updatedBy', { name: policy.updated_by })}
          </Badge>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="default"
          onClick={handleSave}
          disabled={update.isPending}
        >
          {update.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('policy.drSave')}
        </Button>
      </div>
    </div>
  );
}

export default PlatformDrLlmPolicyPage;
